import { App } from '../../App';
import {
  buildTinkWaitingSearch,
  getTinkReportsFromCookies,
  hasTinkReportIds,
} from 'utils/tinkReports';
import { Response as GetApplication } from 'api/typegen/client/application/get';
import { Response as GetClient } from 'api/typegen/client/get';
import { getFetchHeaders } from 'utils/fetchHeaders';
import { isValidApplication } from 'utils/applicationUtility';
import { ROUTES } from 'utils/constants/constants_routes';
import { useCallback } from 'react';
import { useCustomNavigation } from 'hooks/useCustomNavigation';
import { useErrorManager } from 'InternalLibrary/app';
import { useNavigate } from 'react-router-dom';
import { useWebcode } from 'hooks/useWebcode';

const useMandatoryFlowRedirection = () => {
  const apiAffiliateEventQualified = App.useApi(
    'affiliate-events/qualified',
    'POST',
  );
  const { isWebCodeRequired } = useWebcode();
  const { setError } = useErrorManager();
  const { navigateToOnlineBankVerification, onlineBankVerifyRoute } =
    useCustomNavigation();
  const navigate = useNavigate();

  const navigateToNextMandatoryStep = useCallback(
    async ({
      application,
      client,
      skipDefaultNavigation = false,
      createApplication,
    }: {
      client: GetClient | null | undefined;
      application: GetApplication | null | undefined;
      skipDefaultNavigation?: boolean;
      createApplication?: (nextRoute: string) => Promise<unknown>;
    }) => {
      const {
        bankAccount,
        hasAttachments,
        firstName,
        lastName,
        dateOfBirth,
        actualAddress,
        employer,
      } = client || {};
      let isPhoneVerificationRequired = true;

      // If client has opened applicaiton, we should already know the name so this shouldn't be needed

      if (!firstName || !lastName || !dateOfBirth) {
        navigate(ROUTES.FIRST_LOAN_PERSONAL_DATA);
        return true;
      }

      if (!actualAddress?.fullAddress) {
        navigate(ROUTES.FIRST_LOAN_ADDRESS);
        return true;
      }

      if (!employer?.employmentStatus) {
        navigate(ROUTES.FIRST_LOAN_INCOME);
        return true;
      }

      try {
        const response = await isWebCodeRequired();
        if (response !== undefined) isPhoneVerificationRequired = response;
      } catch {
        setError('login_unavailable');
      }

      if (createApplication && !isValidApplication(application, client)) {
        await createApplication(
          isPhoneVerificationRequired
            ? ROUTES.FIRST_LOAN_PHONE_VERIFICATION
            : onlineBankVerifyRoute,
        );
        return true;
      }

      if (isPhoneVerificationRequired) {
        navigate(ROUTES.FIRST_LOAN_PHONE_VERIFICATION);
        return true;
      }

      if (client?.status === 'IDENTIFIED' || (hasAttachments && bankAccount)) {
        navigate(ROUTES.FIRST_LOAN_MANUAL_UPLOAD_WAITING);
        return true;
      }

      if (!application?.confirmed) {
        navigate(ROUTES.FIRST_LOAN_PHONE_VERIFICATION_WAITING);
        return true;
      } else if (client?.isRegisteredByAffiliate) {
        await apiAffiliateEventQualified.fetch({
          headers: getFetchHeaders(),
        });
      }
      if (skipDefaultNavigation) return false;

      // Continue tink flow
      const pendingIds = getTinkReportsFromCookies();
      if (hasTinkReportIds(pendingIds)) {
        navigate(
          `${
            ROUTES.FIRST_LOAN_ONLINE_BANK_TINK_WAITING
          }?${buildTinkWaitingSearch(pendingIds)}`,
        );
        return true;
      }

      navigateToOnlineBankVerification();
      return false;
    },
    [
      navigate,
      isWebCodeRequired,
      setError,
      navigateToOnlineBankVerification,
      onlineBankVerifyRoute,
      apiAffiliateEventQualified,
    ],
  );

  return {
    navigateToNextMandatoryStep,
  };
};

export default useMandatoryFlowRedirection;
